import { CSSProperties, useRef } from 'react';
import { useAnimatedReaction, useSharedValue } from 'react-native-reanimated';

import { EdgeFadeProps } from './EdgeFade';

// tsconfig omits the "dom" lib, so @types/react's HTMLDivElement stub has no style property
type StyleableDiv = { style: { maskImage: string; webkitMaskImage: string } };

const mask = (left: number, right: number, leftProgress: number, rightProgress: number) =>
    `linear-gradient(to right,` +
    ` rgba(255, 255, 255, ${1 - leftProgress}) 0px,` +
    ` #fff ${left}px,` +
    ` #fff calc(100% - ${right}px),` +
    ` rgba(255, 255, 255, ${1 - rightProgress}) 100%)`;

// MaskedView's web build renders the mask instead of the children, so mask with css here. On a plain div
// because react-native-web compiles View styles into atomic css, unreliable for non-standard properties.
export const EdgeFade = ({ width, left = 0, right = 0, leftProgress, rightProgress, children }: EdgeFadeProps) => {
    const fullyOn = useSharedValue(1);
    const leftP = leftProgress ?? fullyOn;
    const rightP = rightProgress ?? fullyOn;
    const ref = useRef<HTMLDivElement>(null);

    const maskImage = mask(left, right, leftP.value, rightP.value);

    useAnimatedReaction(
        () => ({ left: leftP.value, right: rightP.value }),
        ({ left: leftValue, right: rightValue }) => {
            const node = ref.current as unknown as StyleableDiv | null;
            if (!node) {
                return;
            }
            const image = mask(left, right, leftValue, rightValue);
            node.style.maskImage = image;
            // CSSOM casing, not React's `WebkitMaskImage`
            node.style.webkitMaskImage = image;
        }
    );

    const style = {
        width,
        height: '100%',
        overflow: 'hidden',
        // anchors absolutely positioned children
        position: 'relative',
        display: 'flex',
        maskImage,
        WebkitMaskImage: maskImage,
    } as CSSProperties;

    return (
        <div ref={ref} style={style}>
            {children}
        </div>
    );
};
